"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
interface CategoryDivision {
  id: string;
  title: string;
  scopeNote: string;
  specs: string;
  ctaText: string;
  to: string;
  imgSrc: string;
  imgSrcset: string;
  imgSizes: string;
  imgAlt: string;
}
const divisions: CategoryDivision[] = [{
  id: "new-units",
  title: "New Mechanical Bulls",
  scopeNote: "Browse new mechanical bulls and bull riding machines with visible pricing and product-specific information.\n\n",
  specs: "3.0 HP Variable VFD | 16ft & 20ft Round Arenas",
  ctaText: "View new machinery",
  to: "/NewMechanicalBullsPage",
  imgSrc: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/caa3bce0-8461-430c-bcdc-1046a18f41e2.png?v=8ls239myrsf",
  imgSrcset: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/caa3bce0-8461-430c-bcdc-1046a18f41e2.png?v=9z8yj9sok07-wvc-srcset",
  imgSizes: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/caa3bce0-8461-430c-bcdc-1046a18f41e2.png?v=pd84pa1euv8-wvc-sizes",
  imgAlt: "Pristine new commercial mechanical bull system with operator console"
}, {
  id: "used-units",
  title: "Used Mechanical Bulls",
  scopeNote: "A used mechanical bull can make sense when the condition and history justify the price. Our used listings are designed around the exact unit being offered. ",
  specs: "Certified Gearboxes | Full Electrical Diagnostic Log",
  ctaText: "View pre-owned inventory",
  to: "/UsedMechanicalBullsPage",
  imgSrc: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/a3f139b8-43d0-4e6f-a632-66922b932f13.png?v=o3uj0gf426f",
  imgSrcset: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/a3f139b8-43d0-4e6f-a632-66922b932f13.png?v=2ozgcz2fqhu-wvc-srcset",
  imgSizes: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/a3f139b8-43d0-4e6f-a632-66922b932f13.png?v=g66rrwpm6yk-wvc-sizes",
  imgAlt: "Pre-owned mechanical bull equipment"
}, {
  id: "parts-accessories",
  title: "Parts & Accessories",
  scopeNote: "Browse parts and accessories including controllers, control boxes, inflatable safety areas, blowers, remotes, replacement components and other equipment when available.\n",
  specs: "Standard In-Stock SKUs | Freight Dispatch Ready",
  ctaText: "Browse components",
  to: "/MechanicalBullPartsAndAccessoriesPage",
  imgSrc: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/60e5876b-db36-43c9-a05e-7fc5df41f49e.png?v=frse7rvexoc",
  imgSrcset: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/60e5876b-db36-43c9-a05e-7fc5df41f49e.png?v=a46tseot4l-wvc-srcset",
  imgSizes: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/60e5876b-db36-43c9-a05e-7fc5df41f49e.png?v=5grnjg6wzgq-wvc-sizes",
  imgAlt: "Heavy-duty electric drive motors, couplings, and industrial safety switches"
}, {
  id: "inflatable-bulls",
  title: "Inflatable Mechanical Bulls",
  scopeNote: "Compare powered mechanical bull systems that use inflatable safety arenas, including package contents, blower considerations, setup requirements and current available equipment.",
  specs: "Commercial 18oz PVC | High-CFM Continuous Blowers",
  ctaText: "View Inflatable Bulls",
  to: "/InflatableMechanicalBullsPage",
  imgSrc: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/996a6f2a-9561-4d43-9801-5230bf92483a.png?v=ublit0etevd",
  imgSrcset: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/996a6f2a-9561-4d43-9801-5230bf92483a.png?v=dt5ay0oqnaf-wvc-srcset",
  imgSizes: "https://mechanicalbullshop.com/wp-content/uploads/2026/09/996a6f2a-9561-4d43-9801-5230bf92483a.png?v=cy43wwq213h-wvc-sizes",
  imgAlt: "Inflatable mechanical bull safety arena"
}];
export default function Categories() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="shop-new-used-parts" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 border-b border-border overflow-hidden">
      <div className="max-w-[80rem] mx-auto px-5 sm:px-10 lg:px-16">
        {/* Section Header */}
        <div className="mb-12 md:mb-16 border-b border-border pb-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div>
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground block mb-2">
              Primary Divisions
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground">
              Shop Mechanical Bulls by Category
            </h2>
          </div>
          <p className="font-sans text-muted-foreground text-sm sm:text-base max-w-md leading-relaxed">
            Different buyers start in different places. Some look for turnkey new machinery, while others need a complete inflatable package with arena walls and blowers. Many reduce initial costs with inspected used units, and active operators source replacement parts, controllers, and accessories.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {divisions.map((item, i) => {
          return <motion.div key={item.id} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.15
          }} transition={{
            duration: 0.35,
            delay: i * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className="flex flex-col h-full">
                <Card className="flex-1 flex flex-col justify-between rounded-none border border-border bg-card hover:border-foreground transition-[border-color] duration-150 p-0 shadow-none group">
                  <div>
                    {/* Visual Media Header */}
                    <div className="relative border-b border-border bg-muted overflow-hidden">
                      <AspectRatio ratio={16 / 10}>
                        <img src={item.imgSrc} data-wvc-srcset={item.imgSrcset} data-wvc-sizes={item.imgSizes} alt={item.imgAlt} loading="lazy" decoding="async" className="w-full h-full object-contain scale-100 group-hover:scale-110 transition-transform duration-500" />
                      </AspectRatio>
                    </div>

                    {/* Card Content Description */}
                    <CardContent className="p-6">
                      <h3 className="font-serif text-2xl font-bold uppercase tracking-tight text-foreground mb-3 group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>

                      <p className="font-sans text-sm text-foreground/80 leading-relaxed mb-4 min-h-[40px]">
                        {item.scopeNote}
                      </p>
                    </CardContent>
                  </div>

                  {/* Actions Area */}
                  <div className="p-6 pt-0 mt-auto">
                    <Button asChild variant="outline" className="w-full justify-between rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background font-serif text-base uppercase tracking-wider h-11 px-4 transition-colors">
                      <Link to={item.to} className="flex items-center justify-between w-full">
                        <span>{item.ctaText}</span>
                        <ArrowRight className="size-4 ml-2" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}